import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

export const PAGE_SIZE = 9;

/** Client-side pagination synced with ?page= in the URL. */
export const usePagination = (items, pageSize = PAGE_SIZE) => {
  const [params, setParams] = useSearchParams();
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const raw = parseInt(params.get("page") || "1", 10);
  const page = Math.min(Math.max(Number.isNaN(raw) ? 1 : raw, 1), totalPages);

  const setPage = (p) => {
    const next = new URLSearchParams(params);
    if (p <= 1) next.delete("page");
    else next.set("page", String(p));
    setParams(next);
  };

  // Scroll to top AFTER the new page has rendered (avoids interrupted smooth scroll)
  const prevPage = useRef(page);
  useEffect(() => {
    if (prevPage.current !== page) window.scrollTo({ top: 0, behavior: "auto" });
    prevPage.current = page;
  }, [page]);

  // Keep URL in sync when ?page= is out of range (only once data is loaded)
  useEffect(() => {
    if (items.length > 0 && params.get("page") && String(page) !== params.get("page")) {
      const next = new URLSearchParams(params);
      if (page <= 1) next.delete("page");
      else next.set("page", String(page));
      setParams(next, { replace: true });
    }
  }, [items.length, page, params, setParams]);

  const resetPage = () => {
    if (params.get("page")) {
      const next = new URLSearchParams(params);
      next.delete("page");
      setParams(next, { replace: true });
    }
  };

  const pageItems = items.slice((page - 1) * pageSize, page * pageSize);
  return { page, totalPages, pageItems, setPage, resetPage, total: items.length, pageSize };
};
